<template>
  <div class="component-layout">
    <div class="flex flex-col space-y-6">
      <!-- Header Section -->
      <div class="bg-white rounded-lg shadow-md p-4">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 class="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <span class="material-symbols-outlined text-primary">auto_fix_high</span> İyileştirme Önerileri Yönetimi </h2>
            <p class="text-sm text-gray-600 mt-1">
              İçerikleriniz için iyileştirme önerilerini yönetin ve takip edin. </p>
          </div>
        </div>
      </div>

      <!-- Filters Section -->
      <div class="bg-white rounded-lg shadow-md p-4">
        <h3 class="text-sm font-semibold text-gray-700 mb-3 flex items-center">
          <span class="material-symbols-outlined text-primary mr-2">filter_alt</span>
          Filtreler
        </h3>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">İçerik Tipi</label>
            <v-select 
              v-model="filters.contentType" 
              :options="contentTypeOptions" 
              @update:modelValue="handleFilterChange" 
              placeholder="Tüm İçerik Tipleri" 
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">İçerik Kategorisi</label>
            <v-select 
              v-model="filters.contentCategory" 
              :options="contentCategoryOptions" 
              @update:modelValue="handleFilterChange" 
              placeholder="Tüm Kategoriler" 
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Öncelik</label>
            <v-select 
              v-model="filters.priority" 
              :options="priorityOptions" 
              @update:modelValue="handleFilterChange" 
              placeholder="Tüm Öncelikler" 
            />
          </div>
        </div>
        
        <div class="flex justify-end mt-3">
          <v-button 
            button-text="Filtreleri Temizle" 
            color="secondary" 
            size="sm" 
            icon="filter_alt_off" 
            @click="resetFilters" 
            v-if="isFiltered"
          />
        </div>
      </div>

      <!-- Statistics Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div class="bg-white rounded-lg shadow-md p-4">
          <div class="flex items-center">
            <div class="w-10 h-10 rounded-full flex items-center justify-center bg-primary">
              <span class="material-symbols-outlined text-white">insights</span>
            </div>
            <div class="ml-4">
              <h3 class="text-sm font-medium text-gray-500">Toplam Öneri</h3>
              <p class="text-xl font-semibold mt-0.5">{{ pagination.total || 0 }}</p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg shadow-md p-4">
          <div class="flex items-center">
            <div class="w-10 h-10 rounded-full flex items-center justify-center bg-blue-500">
              <span class="material-symbols-outlined text-white">image</span>
            </div>
            <div class="ml-4">
              <h3 class="text-sm font-medium text-gray-500">Görsel Önerileri</h3>
              <p class="text-xl font-semibold mt-0.5">{{ imageCount }}</p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg shadow-md p-4">
          <div class="flex items-center">
            <div class="w-10 h-10 rounded-full flex items-center justify-center bg-green-500">
              <span class="material-symbols-outlined text-white">text_format</span>
            </div>
            <div class="ml-4">
              <h3 class="text-sm font-medium text-gray-500">Metin Önerileri</h3>
              <p class="text-xl font-semibold mt-0.5">{{ textCount }}</p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg shadow-md p-4">
          <div class="flex items-center">
            <div class="w-10 h-10 rounded-full flex items-center justify-center bg-red-500">
              <span class="material-symbols-outlined text-white">videocam</span>
            </div>
            <div class="ml-4">
              <h3 class="text-sm font-medium text-gray-500">Video Önerileri</h3>
              <p class="text-xl font-semibold mt-0.5">{{ videoCount }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Suggestions List -->
      <div class="bg-white rounded-lg shadow-md overflow-hidden">
        <div class="border-b border-gray-200 px-4 py-3 flex justify-between items-center">
          <h3 class="text-sm font-semibold text-gray-700 flex items-center">
            <span class="material-symbols-outlined text-primary mr-2">list</span>
            Öneriler Listesi
          </h3>
          <div class="text-xs text-gray-500">
            Toplam: {{ pagination.total || 0 }} öneri
          </div>
        </div>

        <div class="overflow-x-auto">
          <suggestions-list 
            :suggestions="suggestions" 
            :loading="loading" 
            :pagination="pagination" 
            @page-change="handlePageChange" 
            @view-suggestion="openViewModal" 
            @edit-suggestion="openEditModal" 
            @delete-suggestion="confirmDeleteSuggestion"
          />
        </div>
      </div>
    </div>

    <!-- Modals -->
    <view-suggestion-modal 
      v-model="modals.view.show" 
      :suggestion="modals.view.data" 
      @edit="() => openEditModal(modals.view.data)"
    />

    <edit-suggestion-modal 
      v-model="modals.edit.show" 
      :suggestion="modals.edit.data" 
      @updated="handleSuggestionUpdated"
    />

    <v-modal 
      v-model="modals.delete.show" 
      modal-title="Öneri Silme" 
      icon="delete" 
      size="dialog" 
      z-index="100000"
    >
      <div class="p-4">
        <div class="bg-red-50 rounded-lg p-4 mb-4 border border-red-100">
          <div class="flex items-start">
            <span class="material-symbols-outlined text-red-500 mr-2">warning</span>
            <div>
              <p class="text-sm text-red-700 font-medium">Öneriyi silmek istediğinizden emin misiniz?</p>
              <p class="text-xs text-red-600 mt-1">Bu işlem geri alınamaz.</p>
            </div>
          </div>
        </div>
        
        <div class="flex justify-end gap-2">
          <v-button button-text="İptal" color="secondary" @click="modals.delete.show = false" size="sm"/>
          <v-button button-text="Sil" color="danger" :is-loading="deletingInProgress" @click="deleteSuggestion" size="sm"/>
        </div>
      </div>
    </v-modal>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import VSelect from '@/components/base/inputs/VSelect.vue'
import VModal from '@/components/modals/VModal.vue'
import VButton from '@/components/base/buttons/VButton.vue'
import SuggestionsList from '@/components/suggestions/components/SuggestionsList.vue'
import ViewSuggestionModal from '@/components/suggestions/components/modals/ViewSuggestionModal.vue'
import EditSuggestionModal from '@/components/suggestions/components/modals/EditSuggestionModal.vue'
import { useSuggestionsManagement } from '@/components/suggestions/composables/use-suggestions-management'
import { useAssetStore } from '@/stores/asset'
import {
  CONTENT_TYPES,
  CONTENT_CATEGORIES,
} from '@/components/suggestions/constants/suggestions'

const { t } = useI18n()
const assetStore = useAssetStore()

const {
  suggestions,
  pagination,
  loading,
  filters,
  fetchSuggestions,
  deleteSuggestion: deleteSuggestionAction,
  allSuggestions,
  applyFiltersAndPagination,
  setFilters
} = useSuggestionsManagement()

// Filtre seçenekleri
const contentTypeOptions = [
  { value: '', label: 'Tümü' },
  { value: CONTENT_TYPES.IMAGE, label: 'Görsel' },
  { value: CONTENT_TYPES.TEXT, label: 'Metin' },
  { value: CONTENT_TYPES.VIDEO, label: 'Video' },
]

const contentCategoryOptions = [
  { value: '', label: 'Tümü' },
  { value: CONTENT_CATEGORIES.SOCIAL_MEDIA, label: 'Sosyal Medya' },
  { value: CONTENT_CATEGORIES.BLOG, label: 'Blog' },
  { value: CONTENT_CATEGORIES.ASSISTANT, label: 'Asistan' },
]

const priorityOptions = [
  { value: '', label: 'Tümü' },
  { value: '1', label: 'Çok Düşük (1)' },
  { value: '2', label: 'Düşük (2)' },
  { value: '3', label: 'Orta (3)' },
  { value: '4', label: 'Yüksek (4)' },
  { value: '5', label: 'Çok Yüksek (5)' },
]

// Modal durumları
const modals = reactive({
  view: {
    show: false,
    data: null,
  },
  edit: {
    show: false,
    data: null,
  },
  delete: {
    show: false,
    data: null,
  },
})

// Silme işlemi durumu
const deletingInProgress = ref(false)

// Computed Properties
const imageCount = computed(() => 
  allSuggestions.value.filter(s => s.contentType === CONTENT_TYPES.IMAGE).length
)

const textCount = computed(() => 
  allSuggestions.value.filter(s => s.contentType === CONTENT_TYPES.TEXT).length
)

const videoCount = computed(() => 
  allSuggestions.value.filter(s => s.contentType === CONTENT_TYPES.VIDEO).length
)

const isFiltered = computed(() => 
  !!filters.contentType || !!filters.contentCategory || !!filters.priority
)

// Metodlar
const handlePageChange = (page) => {
  pagination.page = page
  refreshSuggestionsWithFilters()
}

// Filtre değişikliklerini ele al
const handleFilterChange = () => {
  // Filtreleme yapıldığında, sayfa numarası 1'e dön
  pagination.page = 1
  refreshSuggestionsWithFilters()
}

// Filtreleri sıfırla
const resetFilters = () => {
  filters.contentType = ''
  filters.contentCategory = ''
  filters.priority = ''
  pagination.page = 1
  refreshSuggestionsWithFilters()
}

// Filtreleri ve sayfalamayı yeniden uygula
const refreshSuggestionsWithFilters = () => {
  if (allSuggestions.value.length > 0) {
    // Composable'daki setFilters metodunu kullan
    setFilters({
      contentType: filters.contentType,
      contentCategory: filters.contentCategory,
      priority: filters.priority
    })
  } else {
    // Eğer veri yoksa API'den getir
    fetchSuggestions({
      contentType: filters.contentType,
      contentCategory: filters.contentCategory,
      priority: filters.priority
    })
  }
}

const openViewModal = (suggestion) => {
  modals.view.data = suggestion
  modals.view.show = true
}

const openEditModal = (suggestion) => {
  modals.edit.data = suggestion
  modals.edit.show = true
}

const confirmDeleteSuggestion = (suggestion) => {
  modals.delete.data = suggestion
  modals.delete.show = true
}

const deleteSuggestion = async () => {
  if (!modals.delete.data) return

  try {
    deletingInProgress.value = true
    await deleteSuggestionAction(modals.delete.data.id)
    modals.delete.show = false
    // Listeyi yenile
    await fetchSuggestions(filters)
  } catch (error) {
    console.error('Error deleting suggestion:', error)
  } finally {
    deletingInProgress.value = false
  }
}

const handleSuggestionUpdated = async () => {
  modals.edit.show = false
  await fetchSuggestions(filters)
}

// Settings entegrasyonu için gerekli save metodu
const save = async () => {
  // Settings entegrasyonu için - burada kaydetme işlemi yok
  return true
}

// Sayfa yüklendiğinde verileri getir
onMounted(async () => {
  try {
    // Asset ID'yi ayarla
    filters.assetId = assetStore.assetId
    
    // Önce tüm verileri getir
    await fetchSuggestions({
      assetId: assetStore.assetId
    })
    
    // Eğer filtreler için seçim varsa bunları uygula
    if (filters.contentType || filters.contentCategory || filters.priority) {
      setFilters({
        contentType: filters.contentType,
        contentCategory: filters.contentCategory,
        priority: filters.priority
      })
    }
  } catch (error) {
    console.error('Error during component initialization:', error)
  }
})

// Save metodunu dışa aktar (settings entegrasyonu için)
defineExpose({ save })
</script>
